import flatten from 'lodash/flatten';
import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { twc } from 'react-twc';

import { BWhiteArrowImg, FrameImg, RtBlueArrowImg, YesImg } from '@/assets';
import config from '@/config';
import { SymbolInput } from '@/config/type';
import RestakeTabContext from '@/context/RestakeTabContext';
import SelectedAssets from '@/context/SelectedAssets';
import SelectedNetwork from '@/context/SelectedNetwork';
import SymbolInputContext from '@/context/SymbolInputContext';
import useQuery from '@/hooks/useQuery';
import { formatTokenNameInput, isChainNativeToken } from '@/utils/regex';
import { cn } from '@/utils/styles';

import BoostTag from '../boost-tag';
import {
  GetLink,
  InputAreaContainer,
  TokenInputContainer,
  TokenInputIcon,
  TokenInputSelectText,
  TokenLabel,
} from './styles';

const { useSymbolInputTracked } = SymbolInputContext;
const { useRestakeTabTracked } = RestakeTabContext;
const { useSelectedAssetsTracked } = SelectedAssets;
const { useSelectedNetworkTracked } = SelectedNetwork;

function TokenSelectInput() {
  const { t } = useTranslation();

  const [symbolInput] = useSymbolInputTracked();
  const [restakeTab] = useRestakeTabTracked();

  const [select, setSelect] = useState(false);

  if (!symbolInput) return null;

  return (
    <InputAreaContainer>
      <TokenLabel>{t('restake.selectToken')}</TokenLabel>
      <TokenInputContainer
        className={cn(select && 'token-input-shadow')}
        onClick={() => setSelect(!select)}
      >
        <TokenInputIcon src={symbolInput.icon} width={20} height={20} />
        <TokenInputSelectText>{formatTokenNameInput(symbolInput.name)}</TokenInputSelectText>
        <TokenInputIcon
          className={cn(select && 'rotate-180')}
          src={BWhiteArrowImg}
          width={20}
          height={20}
        />
        <TokenInputDropdown show={select} />
      </TokenInputContainer>
      {restakeTab === 'deposit' && symbolInput.url && !isChainNativeToken(symbolInput.name) && (
        <GetLink href={symbolInput.url} target="_blank">
          <p>
            {t('restake.get')} ${symbolInput.symbol}
          </p>
          <TokenInputIcon src={RtBlueArrowImg} width={20} height={20} />
        </GetLink>
      )}
    </InputAreaContainer>
  );
}

const TokenInputDropdown = ({ show }: { show: boolean }) => {
  const [symbolInput, setSymbolInputTracked] = useSymbolInputTracked();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const tab = query.get('tab');
  const [selectedAssets, setSelectedAssets] = useSelectedAssetsTracked();
  const [selectedNetwork, setSelectedNetwork] = useSelectedNetworkTracked();

  const { t } = useTranslation();

  if (!symbolInput) return null;
  return (
    <TokenInputDropdownContainer className={cn(!show && 'opacity-0 pointer-events-none')}>
      <div
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <FilterRow>
          <FilterTitle>{t('restake.network')}</FilterTitle>
          {config.dropdownNetworks
            .filter((network) =>
              flatten(
                config.restakingTokens
                  .filter((token) =>
                    tab === 'unstake' ? token.showInUnstakeList : token.showInStakeList,
                  )
                  .map((token) => token.selectTag),
              ).includes(network),
            )
            .map((network) => {
              return (
                <FilterTag
                  key={network}
                  selected={selectedNetwork.includes(network)}
                  onClick={() => {
                    setSelectedNetwork((prev) => {
                      if (prev.length === config.dropdownNetworks.length) {
                        return [network];
                      } else if (prev.includes(network)) {
                        const filteredResult = prev.filter((n) => n !== network);
                        if (filteredResult.length === 0) {
                          return config.dropdownNetworks;
                        } else {
                          return filteredResult;
                        }
                      } else {
                        return [...prev, network];
                      }
                    });
                  }}
                >
                  {network.toUpperCase()}
                </FilterTag>
              );
            })}
        </FilterRow>
        <FilterRow>
          <FilterTitle>{t('restake.assets')}</FilterTitle>
          {config.dropdownAssets.map((asset) => {
            const disabled = !flatten(
              config.restakingTokens
                .filter((token) =>
                  tab === 'unstake' ? token.showInUnstakeList : token.showInStakeList,
                )
                .filter((token) => selectedNetwork.includes(token.chain))
                .map((token) => token.selectTag),
            ).includes(asset);

            return (
              <FilterTag
                key={asset}
                icon={asset === 'Bithive' ? FrameImg : ''}
                disabled={disabled}
                selected={selectedAssets.includes(asset)}
                onClick={() => {
                  setSelectedAssets((prev) => {
                    if (prev.length === config.dropdownAssets.length) {
                      return [asset];
                    } else if (prev.includes(asset)) {
                      const filteredResult = prev.filter((n) => n !== asset);
                      if (filteredResult.length === 0) {
                        return config.dropdownAssets;
                      } else {
                        return filteredResult;
                      }
                    } else {
                      return [...prev, asset];
                    }
                  });
                }}
              >
                {asset}
              </FilterTag>
            );
          })}
        </FilterRow>
      </div>
      <hr className="h-[1px] border-none bg-[#FFFFFF24] my-2" />
      <div className="px-1 max-h-[250px] overflow-auto">
        {config.restakingTokens
          .filter((token) => (tab === 'unstake' ? token.showInUnstakeList : token.showInStakeList))
          .filter(
            (token) =>
              token.selectTag.find((tag) => selectedAssets.includes(tag)) &&
              token.selectTag.find((tag) => selectedNetwork.includes(tag)),
          )
          .map((token: SymbolInput, idx) => (
            <div
              className={cn(
                'p-2 flex gap-2 rounded-md flex-col',
                'hover:bg-[#1F242F] duration-100 cursor-pointer',
                {
                  'bg-[#1F242F]': symbolInput.name === token.name,
                },
              )}
              key={idx}
              onClick={() => {
                setSymbolInputTracked(token);
                navigate(`${location.pathname}?symbol=${token.name}${tab ? `&tab=${tab}` : ''}`);
              }}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <img
                    className="overflow-hidden rounded-full w-5 h-5 sm:w-[22px] sm:h-[22px]"
                    src={token.icon}
                  />
                  <p>{formatTokenNameInput(token.name)}</p>
                  <div className="items-center hidden gap-1 ml-5 sm:flex">
                    {token.boostTag.map((tag) => (
                      <BoostTag key={tag}>{tag}</BoostTag>
                    ))}
                  </div>
                </div>
                {symbolInput.name === token.name && <img src={YesImg} alt="Yes" />}
              </div>
              <div className="flex flex-wrap items-center gap-1 sm:hidden">
                {token.boostTag.map((tag) => (
                  <BoostTag key={tag}>{tag}</BoostTag>
                ))}
              </div>
            </div>
          ))}
      </div>
    </TokenInputDropdownContainer>
  );
};

const FilterTag = ({
  children,
  selected,
  onClick,
  disabled,
  icon,
}: PropsWithChildren & {
  selected?: boolean;
  onClick: () => void;
  disabled?: boolean;
  icon?: string;
}) => {
  return (
    <div
      className={cn(
        'px-1.5 py-0.5 sm:py-1 text-white text-sm rounded-lg flex items-center',
        selected && !disabled ? 'bg-[#4565E5]' : 'bg-[#1F242F]',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
      onClick={() => {
        if (!disabled) {
          onClick();
        }
      }}
    >
      {icon && <img src={icon} alt="icon" className="mr-[1px]" />}
      {children}
    </div>
  );
};

const FilterTitle = twc.div`
  text-white 123
`;

const FilterRow = twc.div`
  py-1.5 sm:py-2 px-3 sm:px-4 flex gap-2.5 cursor-default flex-wrap
`;

const TokenInputDropdownContainer = twc.div`
  absolute z-10 left-0 top-[60px] md:top-[70px] w-full
  bg-[#0C111D] rounded-md
  flex flex-col
  border border-[#1F242F] py-1
  duration-300
`;

export default TokenSelectInput;
